import { useEffect, useMemo, useState } from "react";
import { Layers, Plus } from "lucide-react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, ApiError } from "../lib/api";
import { fmtDate, num, rm } from "../lib/format";
import { toast } from "../stores/toast";
import { useAuth } from "../stores/auth";
import { useBottomBarActions } from "../hooks/useBottomBarActions";
import {
  Badge,
  Button,
  Input,
  SearchSelect,
  Textarea,
  Toggle,
  statusColor,
  useConfirm,
} from "../components/ui";
import { DataTable, type Column } from "../components/DataTable";
import { ListPageShell } from "../components/shell/ListPageShell";
import { flattenInfinitePages, getNextPageParam } from "../lib/infinite-list";
import { showQueryLoader } from "../lib/query-utils";
import { ImageHoverPreview } from "../components/ImageHoverPreview";
import { RightSidebarPanel } from "../components/RightSidebarPanel";
import { ImageUploadField } from "../components/ImageUploadField";
import { ThumbnailBackgroundField } from "../components/ThumbnailBackgroundField";
import { useQueryRecordId } from "../lib/use-query-record";
import { categoryPathMap, categoryPathOptions } from "../lib/categoryPaths";
import type { ProductBehavior, ProductTypeDefinitionDto } from "@gaole/shared";

interface CategoryOption {
  id: string;
  name: string;
  parentId: string | null;
}

interface ProductRow {
  id: string;
  sku: string;
  name: string;
  slug: string;
  description: string | null;
  type: string;
  typeLabel?: string;
  behavior?: ProductBehavior;
  price: number;
  costPrice: number | null;
  creditPrice: number | null;
  images: string[];
  categoryId: string | null;
  categoryName: string | null;
  stockAvailable: number;
  stockOnHand?: number;
  stockReserved?: number;
  weightGrams: number;
  active: boolean;
  imageBackgroundColor?: string | null;
  wishlistCount?: number;
  createdAt: string;
}

interface ProductForm {
  sku: string;
  name: string;
  description: string;
  type: string;
  price: string;
  costPrice: string;
  creditPrice: string;
  images: string[];
  categoryId: string;
  weightGrams: string;
  imageBackgroundColor: string;
  active: boolean;
  initialStock: string;
  stockOnHand: string;
  stockAdjustNote: string;
}

const PAGE_SIZE = 25;

type ProductStatusTab = "active" | "inactive";

const STATUS_TABS: { value: ProductStatusTab; label: string }[] = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

function emptyForm(): ProductForm {
  return {
    sku: "",
    name: "",
    description: "",
    type: "SINGLE",
    price: "",
    costPrice: "",
    creditPrice: "",
    images: [],
    categoryId: "",
    weightGrams: "100",
    imageBackgroundColor: "",
    active: true,
    initialStock: "0",
    stockOnHand: "0",
    stockAdjustNote: "",
  };
}

function rowToForm(row: ProductRow): ProductForm {
  return {
    sku: row.sku,
    name: row.name,
    description: row.description ?? "",
    type: row.type,
    price: String(row.price),
    costPrice: row.costPrice != null ? String(row.costPrice) : "",
    creditPrice: row.creditPrice != null ? String(row.creditPrice) : "",
    images: [...row.images],
    categoryId: row.categoryId ?? "",
    weightGrams: String(row.weightGrams),
    imageBackgroundColor: row.imageBackgroundColor ?? "",
    active: row.active,
    initialStock: "0",
    stockOnHand: String(row.stockOnHand ?? row.stockAvailable),
    stockAdjustNote: "",
  };
}

const TYPE_BEHAVIOR_HINTS: Partial<Record<ProductBehavior, string>> = {
  PACK: "Legacy mystery pack SKU — configure new packs under Packs, not Products",
  BUNDLE: "Built from bill-of-materials components",
  SINGLE: "Physical item with inventory and shipping",
};

function tracksStock(behavior: ProductBehavior | undefined): boolean {
  return behavior !== "BUNDLE" && behavior !== "CREDIT_PACK" && behavior !== "PACK";
}

function formToBody(
  form: ProductForm,
  isCreate: boolean,
  editing: ProductRow | null,
  behavior?: ProductBehavior,
) {
  const body: Record<string, unknown> = {
    sku: form.sku.trim(),
    name: form.name.trim(),
    description: form.description.trim() || null,
    type: form.type,
    price: Number.parseFloat(form.price) || 0,
    images: form.images.slice(0, 5),
    categoryId: form.categoryId || null,
    weightGrams: Number.parseInt(form.weightGrams, 10) || 100,
    active: form.active,
    imageBackgroundColor: form.imageBackgroundColor.trim() || null,
  };
  if (form.creditPrice.trim()) body.creditPrice = Number.parseFloat(form.creditPrice);
  if (form.costPrice.trim()) body.costPrice = Number.parseFloat(form.costPrice);
  else if (!isCreate && editing) body.costPrice = null;
  if (isCreate && form.initialStock.trim()) {
    body.initialStock = Number.parseInt(form.initialStock, 10) || 0;
  }
  if (editing && tracksStock(behavior)) {
    const baseline = editing.stockOnHand ?? editing.stockAvailable;
    const nextOnHand = Number.parseInt(form.stockOnHand, 10) || 0;
    if (nextOnHand !== baseline) {
      body.stockOnHand = nextOnHand;
      body.stockAdjustNote = form.stockAdjustNote.trim();
    }
  }
  return body;
}

export function Products() {
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const { confirm, dialog } = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const search = searchParams.get("search") ?? "";
  const status = (searchParams.get("status") as ProductStatusTab | null) ?? "active";

  const [searchInput, setSearchInput] = useState(search);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<ProductRow | null>(null);
  const [form, setForm] = useState<ProductForm>(emptyForm);
  const [deepLinkId, setDeepLinkId] = useState<string | null>(null);
  const { recordId, clearRecordId } = useQueryRecordId();

  const canWrite = can("catalog.write");

  const deepLinkQuery = useQuery({
    queryKey: ["catalog-product", deepLinkId],
    queryFn: () => api<{ product: ProductRow }>(`/catalog/products/${deepLinkId}`),
    enabled: !!deepLinkId,
  });

  useEffect(() => {
    if (!recordId) return;
    setDeepLinkId(recordId);
    clearRecordId();
  }, [recordId, clearRecordId]);

  useEffect(() => {
    const product = deepLinkQuery.data?.product;
    if (!product) return;
    setEditing(product);
    setForm(rowToForm(product));
    setEditorOpen(true);
    setDeepLinkId(null);
  }, [deepLinkQuery.data?.product]);

  const listQuery = useInfiniteQuery({
    queryKey: ["catalog-products", status, search],
    initialPageParam: 1,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({
        page: String(pageParam),
        pageSize: String(PAGE_SIZE),
        includeInactive: "true",
        excludeCreditPacks: "true",
        active: status === "inactive" ? "false" : "true",
      });
      if (search.trim()) params.set("search", search.trim());
      return api<{ items: ProductRow[]; total: number }>(`/catalog/products?${params}`);
    },
    getNextPageParam: (lastPage, allPages) =>
      getNextPageParam(
        lastPage.total,
        allPages.reduce((acc, page) => acc + page.items.length, 0),
        PAGE_SIZE,
      ),
  });

  const productRows = flattenInfinitePages(listQuery.data?.pages);
  const listTotal = listQuery.data?.pages[0]?.total ?? 0;
  const hasNextPage =
    productRows.length < listTotal && (listQuery.hasNextPage ?? false);

  const categoriesQuery = useQuery({
    queryKey: ["catalog-categories"],
    queryFn: () => api<{ categories: CategoryOption[] }>("/catalog/categories"),
    enabled: editorOpen,
  });

  const productTypesQuery = useQuery({
    queryKey: ["catalog-product-types"],
    queryFn: () => api<{ types: ProductTypeDefinitionDto[] }>("/catalog/product-types"),
  });

  const productTypes = useMemo(
    () => (productTypesQuery.data?.types ?? []).filter((t) => t.active),
    [productTypesQuery.data?.types],
  );

  const typeOptions = useMemo(
    () =>
      productTypes
        .filter((t) => t.behavior !== "CREDIT_PACK" && t.behavior !== "PACK")
        .map((t) => ({ value: t.key, label: t.label })),
    [productTypes],
  );

  const categoryOptions = useMemo(
    () => categoryPathOptions(categoriesQuery.data?.categories ?? []).map((c) => ({ value: c.id, label: c.label })),
    [categoriesQuery.data?.categories],
  );
  const categoryPaths = useMemo(
    () => categoryPathMap(categoriesQuery.data?.categories ?? []),
    [categoriesQuery.data?.categories],
  );

  const selectedType = useMemo(
    () => productTypes.find((t) => t.key === form.type) ?? productTypes[0],
    [productTypes, form.type],
  );

  const effectiveBehavior = selectedType?.behavior ?? editing?.behavior;
  const typeChanged = !!editing && form.type !== editing.type;

  const stockBreakdownQuery = useQuery({
    queryKey: ["inventory-breakdown", editing?.id],
    queryFn: () =>
      api<{
        breakdown: {
          onHand: number;
          reserved: number;
          shopAvailable: number;
          warehouseFree: number;
          inPacks: number;
          inRaffles: number;
          inAuctions: number;
          inCheckout: number;
          ledgerIssues: string[];
          commitments: { refType: string; label: string; qtyOpen: number; status: string | null }[];
        };
        stockRepaired: boolean;
      }>(`/inventory/products/${editing!.id}/breakdown`),
    enabled: !!editing?.id && editorOpen && tracksStock(effectiveBehavior),
    select: (data) => data,
  });

  useEffect(() => {
    if (stockBreakdownQuery.data?.stockRepaired) {
      void queryClient.invalidateQueries({ queryKey: ["catalog-products"] });
      void queryClient.invalidateQueries({ queryKey: ["inventory-low-stock"] });
    }
  }, [stockBreakdownQuery.data?.stockRepaired, queryClient]);

  useEffect(() => {
    if (productTypes.length === 0 || editing) return;
    if (!productTypes.some((t) => t.key === form.type)) {
      setForm((f) => ({ ...f, type: productTypes[0]!.key }));
    }
  }, [productTypes, form.type, editing]);

  const saveMutation = useMutation({
    mutationFn: (payload: { id?: string; body: Record<string, unknown> }) => {
      if (payload.id) {
        return api(`/catalog/products/${payload.id}`, { method: "PUT", body: payload.body });
      }
      return api<{ id: string }>("/catalog/products", { method: "POST", body: payload.body });
    },
    onSuccess: () => {
      toast("success", editing ? "Product updated" : "Product created");
      closeEditor();
      void queryClient.invalidateQueries({ queryKey: ["catalog-products"] });
      void queryClient.invalidateQueries({ queryKey: ["inventory-movements"] });
      void queryClient.invalidateQueries({ queryKey: ["inventory-breakdown"] });
    },
    onError: (err) => toast("error", err instanceof ApiError ? err.message : "Save failed"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api(`/catalog/products/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      closeEditor();
      void queryClient.invalidateQueries({ queryKey: ["catalog-products"] });
    },
    onError: (err) => toast("error", err instanceof ApiError ? err.message : "Delete failed"),
  });


  function closeEditor() {
    setEditorOpen(false);
    setEditing(null);
    setForm(emptyForm());
  }

  function openCreate() {
    setEditing(null);
    setForm(emptyForm());
    setEditorOpen(true);
  }

  function openEdit(row: ProductRow) {
    setEditing(row);
    setForm(rowToForm(row));
    setEditorOpen(true);
  }

  function openDuplicate(row: ProductRow) {
    setEditing(null);
    const copied = rowToForm(row);
    setForm({ ...copied, sku: "", name: `${row.name} (copy)` });
    setEditorOpen(true);
  }

  const tableRowActions = useMemo(
    () =>
      canWrite
        ? {
            onEdit: openEdit,
            onDuplicate: openDuplicate,
            onDelete: async (row: ProductRow) => {
              await deleteMutation.mutateAsync(row.id);
            },
          }
        : undefined,
    [canWrite, deleteMutation],
  );

  const tableSelection = useMemo(
    () =>
      canWrite
        ? {
            entityLabel: "Product",
            onEdit: (rows: ProductRow[]) => openEdit(rows[0]!),
            onDuplicate: (rows: ProductRow[]) => rows.forEach(openDuplicate),
            onDelete: async (rows: ProductRow[]) => {
              await Promise.all(rows.map((r) => deleteMutation.mutateAsync(r.id)));
            },
          }
        : undefined,
    [canWrite, deleteMutation],
  );

  function submitProduct() {
    if (editing && tracksStock(effectiveBehavior)) {
      const baseline = editing.stockOnHand ?? editing.stockAvailable;
      const nextOnHand = Number.parseInt(form.stockOnHand, 10) || 0;
      if (nextOnHand !== baseline) {
        const note = form.stockAdjustNote.trim();
        if (note.length < 3) {
          toast("error", "Enter a reason for the stock change (at least 3 characters)");
          return;
        }
      }
    }
    saveMutation.mutate({
      id: editing?.id,
      body: formToBody(form, !editing, editing, effectiveBehavior),
    });
  }

  const stockChanged =
    !!editing &&
    tracksStock(effectiveBehavior) &&
    (Number.parseInt(form.stockOnHand, 10) || 0) !== (editing.stockOnHand ?? editing.stockAvailable);

  useBottomBarActions(() => {
    if (!canWrite) return [];
    if (editorOpen) {
      return [
        { id: "cancel", label: "Cancel", onClick: closeEditor },
        {
          id: "save",
          label: "Save",
          variant: "primary" as const,
          disabled:
            saveMutation.isPending ||
            !form.sku.trim() ||
            !form.name.trim() ||
            (stockChanged && form.stockAdjustNote.trim().length < 3),
          onClick: submitProduct,
        },
      ];
    }
    return [{ id: "create", label: "Create", icon: Plus, variant: "primary" as const, onClick: openCreate }];
  });

  function updateParams(patch: Record<string, string | null>) {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(patch)) {
      if (value == null || value === "") next.delete(key);
      else next.set(key, value);
    }
    setSearchParams(next, { replace: true });
  }

  const columns: Column<ProductRow>[] = useMemo(
    () => [
      {
        key: "name",
        header: "Product",
        sortable: true,
        sortValue: (r) => r.name,
        exportValue: (r) => `${r.name} (${r.sku})`,
        render: (r) => (
          <div className="flex items-center gap-2.5">
            {r.images[0] ? (
              <ImageHoverPreview src={r.images[0]} previewWidth={240} previewHeight={240}>
                <img src={r.images[0]} alt="" className="h-9 w-9 rounded-lg border border-line object-cover" />
              </ImageHoverPreview>
            ) : (
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/5 text-white/50">
                <Layers size={16} />
              </div>
            )}
            <div className="min-w-0">
              <p className="truncate font-medium">{r.name}</p>
              <p className="font-mono text-[10px] text-white/40">{r.sku}</p>
            </div>
          </div>
        ),
      },
      {
        key: "type",
        header: "Type",
        hideSm: true,
        sortable: true,
        sortValue: (r) => r.typeLabel ?? r.type,
        render: (r) => (
          <Badge color="gray">{(r.typeLabel ?? r.type).replace(/_/g, " ")}</Badge>
        ),
      },
      {
        key: "category",
        header: "Category",
        hideSm: true,
        sortable: true,
        sortValue: (r) => (r.categoryId ? categoryPaths.get(r.categoryId) : null) ?? r.categoryName ?? "",
        exportValue: (r) => (r.categoryId ? categoryPaths.get(r.categoryId) : null) ?? r.categoryName ?? "",
        render: (r) => (
          <span className="text-xs text-white/60">
            {(r.categoryId ? categoryPaths.get(r.categoryId) : null) ?? r.categoryName ?? "—"}
          </span>
        ),
      },
      {
        key: "price",
        header: "Price",
        className: "text-right",
        sortable: true,
        sortValue: (r) => r.price,
        exportValue: (r) => r.price,
        render: (r) => <span className="font-display font-semibold">{rm(r.price)}</span>,
      },
      {
        key: "costPrice",
        header: "Cost",
        className: "text-right",
        hideSm: true,
        sortable: true,
        sortValue: (r) => r.costPrice ?? -1,
        exportValue: (r) => r.costPrice ?? "",
        render: (r) => (
          <span className="text-xs text-muted">{r.costPrice != null ? rm(r.costPrice) : "—"}</span>
        ),
      },
      {
        key: "stock",
        header: "Stock",
        className: "text-right",
        sortable: true,
        sortValue: (r) => r.stockAvailable,
        exportValue: (r) => r.stockAvailable,
        render: (r) => (
          <span
            className={`font-display font-semibold tabular-nums ${r.stockAvailable <= 3 ? "text-amber-300" : ""}`}
          >
            {num(r.stockAvailable)}
          </span>
        ),
      },
      {
        key: "wishlist",
        header: "Wishlisted",
        className: "text-right",
        hideSm: true,
        sortable: true,
        sortValue: (r) => r.wishlistCount ?? 0,
        exportValue: (r) => r.wishlistCount ?? 0,
        render: (r) => <span className="text-xs tabular-nums">{num(r.wishlistCount ?? 0)}</span>,
      },
      {
        key: "active",
        header: "Status",
        sortable: true,
        sortValue: (r) => r.active,
        exportValue: (r) => (r.active ? "Active" : "Inactive"),
        render: (r) => (
          <Badge color={r.active ? statusColor("PAID") : statusColor("CANCELLED")}>
            {r.active ? "Active" : "Inactive"}
          </Badge>
        ),
      },
      {
        key: "created",
        header: "Added",
        hideSm: true,
        sortable: true,
        sortValue: (r) => r.createdAt,
        exportValue: (r) => fmtDate(r.createdAt),
        render: (r) => <span className="text-xs text-white/50">{fmtDate(r.createdAt)}</span>,
      },
    ],
    [categoryPaths],
  );

  return (
    <ListPageShell>
      <DataTable
        columns={columns}
        rows={productRows}
        rowKey={(r) => r.id}
        onRowClick={canWrite ? openEdit : undefined}
        loading={showQueryLoader(listQuery)}
        exportFilename="products"
        entityLabel="Product"
        rowActions={tableRowActions}
        selection={tableSelection}
        infiniteScroll={{
          hasNextPage,
          isFetchingNextPage: listQuery.isFetchingNextPage,
          onLoadMore: () => void listQuery.fetchNextPage(),
        }}
        toolbar={{
          search: {
            value: searchInput,
            onChange: setSearchInput,
            onSubmit: () => updateParams({ search: searchInput.trim() || null }),
            placeholder: "Search name, SKU…",
          },
          views: {
            options: STATUS_TABS,
            value: status,
            onChange: (v) => updateParams({ status: v }),
          },
        }}
        empty={{
          icon: Layers,
          title: status === "active" ? "No active products" : "No inactive products",
          hint: search ? "Try clearing search." : canWrite ? "Create your first product." : undefined,
          action: canWrite ? (
            <Button size="sm" onClick={openCreate}>
              Create
            </Button>
          ) : undefined,
        }}
      />

      <RightSidebarPanel
        open={editorOpen}
        title={editing ? "Edit product" : "Create product"}
        subtitle={editing?.sku}
        wide
        onClose={closeEditor}
        footer={
          canWrite && (
            <div className="flex flex-wrap justify-end gap-2">
              {editing && (
                <Button
                  variant="danger"
                  size="sm"
                  disabled={deleteMutation.isPending}
                  onClick={() =>
                    confirm({
                      title: "Delete product?",
                      message: `Remove ${editing.name}? Products with order history will be deactivated instead.`,
                      confirmLabel: "Delete",
                      danger: true,
                      action: async () => {
                        await deleteMutation.mutateAsync(editing.id);
                        toast("success", "Product removed");
                      },
                    })
                  }
                >
                  Delete
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={closeEditor}>
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={
                  saveMutation.isPending ||
                  !form.sku.trim() ||
                  !form.name.trim() ||
                  (stockChanged && form.stockAdjustNote.trim().length < 3)
                }
                onClick={submitProduct}
              >
                Save
              </Button>
            </div>
          )
        }
      >
        <div className="sidebar-form">
          <Input
            label="SKU"
            placeholder="e.g. PKM-001"
            value={form.sku}
            onChange={(e) => setForm((f) => ({ ...f, sku: e.target.value }))}
            disabled={!!editing}
          />
          <Input
            label="Name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          />
          <SearchSelect
            label="Type"
            placeholder="Select type…"
            options={typeOptions}
            value={form.type}
            onChange={(v) => setForm((f) => ({ ...f, type: v, stockAdjustNote: "" }))}
            hint={
              editing
                ? typeChanged
                  ? "Saving will update the product type. Bundle components are cleared when leaving Bundle."
                  : TYPE_BEHAVIOR_HINTS[effectiveBehavior ?? "SINGLE"] ??
                    "Change how this product behaves in warehouse and fulfilment."
                : TYPE_BEHAVIOR_HINTS[selectedType?.behavior ?? "SINGLE"]
            }
          />
          <div className="form-row-2">
            <Input
              label="Price (MYR)"
              type="number"
              min="0"
              step="0.01"
              value={form.price}
              onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
            />
            <Input
              label="Cost price (MYR)"
              type="number"
              min="0"
              step="0.01"
              hint="Internal only"
              value={form.costPrice}
              onChange={(e) => setForm((f) => ({ ...f, costPrice: e.target.value }))}
            />
          </div>
          <Input
            label="Credit price"
            type="number"
            min="0"
            step="0.01"
            hint="Optional"
            value={form.creditPrice}
            onChange={(e) => setForm((f) => ({ ...f, creditPrice: e.target.value }))}
          />
          {!editing && tracksStock(selectedType?.behavior) && (
            <Input
              label="Initial stock"
              type="number"
              min="0"
              value={form.initialStock}
              onChange={(e) => setForm((f) => ({ ...f, initialStock: e.target.value }))}
            />
          )}
          {editing && tracksStock(effectiveBehavior) && (
            <div className="space-y-3">
              <Input
                label="On-hand stock"
                type="number"
                min="0"
                value={form.stockOnHand}
                onChange={(e) => setForm((f) => ({ ...f, stockOnHand: e.target.value }))}
                hint={
                  editing.stockReserved
                    ? `${num(editing.stockReserved)} reserved · ${num(editing.stockAvailable)} available`
                    : `${num(editing.stockAvailable)} available`
                }
              />
              {stockChanged && (
                <Textarea
                  label="Reason for stock change"
                  rows={2}
                  required
                  placeholder="e.g. Received shipment, damaged units removed…"
                  value={form.stockAdjustNote}
                  onChange={(e) => setForm((f) => ({ ...f, stockAdjustNote: e.target.value }))}
                  hint={
                    form.stockAdjustNote.trim().length > 0 && form.stockAdjustNote.trim().length < 3
                      ? "At least 3 characters required"
                      : "Required when changing stock. Recorded in inventory movements."
                  }
                />
              )}
              {stockBreakdownQuery.data?.breakdown && (
                <div className="rounded-xl border border-line bg-white/[0.02] p-3 text-xs">
                  <p className="mb-2 font-semibold uppercase tracking-wider text-white/40">Stock locations</p>
                  <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                    <dt className="text-white/50">Warehouse (on hand)</dt>
                    <dd className="text-right font-display font-semibold tabular-nums">
                      {num(stockBreakdownQuery.data.breakdown.onHand)}
                    </dd>
                    <dt className="text-white/50">WH free</dt>
                    <dd className="text-right font-display font-semibold tabular-nums">
                      {num(stockBreakdownQuery.data.breakdown.warehouseFree)}
                    </dd>
                    <dt className="text-white/50">Shop available</dt>
                    <dd className="text-right font-display font-semibold tabular-nums text-emerald-300">
                      {num(stockBreakdownQuery.data.breakdown.shopAvailable)}
                    </dd>
                    <dt className="text-white/50">In packs</dt>
                    <dd className="text-right font-display font-semibold tabular-nums">{num(stockBreakdownQuery.data.breakdown.inPacks)}</dd>
                    <dt className="text-white/50">In raffles</dt>
                    <dd className="text-right font-display font-semibold tabular-nums">{num(stockBreakdownQuery.data.breakdown.inRaffles)}</dd>
                    <dt className="text-white/50">In auctions</dt>
                    <dd className="text-right font-display font-semibold tabular-nums">{num(stockBreakdownQuery.data.breakdown.inAuctions)}</dd>
                    <dt className="text-white/50">Checkout holds</dt>
                    <dd className="text-right font-display font-semibold tabular-nums">{num(stockBreakdownQuery.data.breakdown.inCheckout)}</dd>
                  </dl>
                  {stockBreakdownQuery.data.breakdown.commitments.length > 0 && (
                    <ul className="mt-3 space-y-1 border-t border-line pt-2">
                      {stockBreakdownQuery.data.breakdown.commitments.map((row) => (
                        <li key={`${row.refType}-${row.label}`} className="flex items-center justify-between gap-2">
                          <span className="truncate text-white/60">
                            <Badge color={row.refType === "PACK" ? "pink" : row.refType === "RAFFLE" ? "blue" : row.refType === "ORDER" ? "green" : "amber"}>
                              {row.refType}
                            </Badge>{" "}
                            {row.label}
                          </span>
                          <span className="shrink-0 font-display font-semibold tabular-nums">{num(row.qtyOpen)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-2"
                    onClick={() =>
                      navigate(`/inventory?tab=allocations&productId=${editing!.id}`)
                    }
                  >
                    View in inventory
                  </Button>
                </div>
              )}
            </div>
          )}
          <SearchSelect
            label="Category"
            placeholder="None"
            searchPlaceholder="Search categories…"
            options={categoryOptions}
            value={form.categoryId}
            onChange={(v) => setForm((f) => ({ ...f, categoryId: v }))}
          />
          <Input
            label="Weight (grams)"
            type="number"
            min="0"
            value={form.weightGrams}
            onChange={(e) => setForm((f) => ({ ...f, weightGrams: e.target.value }))}
          />
          <Textarea
            label="Description"
            rows={3}
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          />
          <ImageUploadField
            label="Images"
            hint="Upload up to 5 images. Singles from Card Uploader use uploader photos."
            value={form.images}
            onChange={(images) => setForm((f) => ({ ...f, images }))}
            max={5}
          />
          <ThumbnailBackgroundField
            value={form.imageBackgroundColor}
            onChange={(imageBackgroundColor) => setForm((f) => ({ ...f, imageBackgroundColor }))}
          />
          <Toggle
            checked={form.active}
            onChange={(v) => setForm((f) => ({ ...f, active: v }))}
            label="Active in warehouse"
          />
        </div>
      </RightSidebarPanel>

      {dialog}
    </ListPageShell>
  );
}
